import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Page } from "../../components";
import LogoutButton from "../../components/LogoutButton";
import { Dict, UserExportDTO } from "@/api/admin/types";
import { escapeCsvField } from "../../utils/csv";
import "./responsive.css";

function arrayToCSV(data: any[], columns: { key: string; label: string }[]) {
  const header = columns.map(col => `"${col.label}"`).join(",");
  const rows = data.map(row =>
    columns.map(col => {
      let val = row[col.key];

      // Format date fields if they're date strings
      if ((col.key === "creationDate" || col.key === "created_at" || col.key === "last_activity_timestamp") && typeof val === "string") {
        try {
          const date = new Date(val);
          if (!isNaN(date.getTime())) {
            val = date.toISOString().replace("T", " ").substring(0, 19);
          }
        } catch (e) {
          // If parsing fails, use the original value
        }
      }

      return escapeCsvField(val);
    }).join(",")
  );
  return [header, ...rows].join("\r\n");
}

function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

const ExportDataPage: React.FC = () => {
  const navigate = useNavigate();
  const [exportStartDate, setExportStartDate] = useState<string>("");
  const [exportEndDate, setExportEndDate] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);
  const [exportMode, setExportMode] = useState<"agency" | "users">("agency");

  const handleExportCSVWithDateRange = async () => {
    if (!exportStartDate || !exportEndDate) {
      alert("Please select both start and end dates");
      return;
    }

    setIsExporting(true);
    try {
      const AdminController = require("../../controllers/AdminController").default;
      // Convert datetime-local format to ISO format for backend
      const startDateISO = new Date(exportStartDate).toISOString();
      const endDateISO = new Date(exportEndDate).toISOString();

      if (exportMode === "users") {
        const exportData: UserExportDTO[] = await AdminController.getUserExport(startDateISO, endDateISO);
        const userColumns: { key: keyof UserExportDTO; label: string }[] = [
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "agency_name", label: "Agency Name" },
          { key: "role", label: "Role" },
          { key: "is_disabled", label: "Disabled" },
          { key: "created_at", label: "Created At" },
          { key: "last_activity_timestamp", label: "Last Activity" },
          { key: "policies_count", label: "Policies Count" },
          { key: "comparisons_count", label: "Comparisons Count" },
          { key: "proposals_count", label: "Proposals Count" },
          { key: "contract_reviews_count", label: "Contract Reviews Count" },
          { key: "gap_analysis_count", label: "Coverage Gap Analysis Count" },
          { key: "single_policy_analysis_count", label: "Single Policy Analysis Count" },
          { key: "sov_count", label: "SOV Count" },
          { key: "loss_runs_count", label: "Loss Runs Count" },
          { key: "coi_count", label: "COI Count" },
          { key: "chats_count", label: "Chats Count" },
        ];
        const csv = arrayToCSV(exportData, userColumns);
        const filename = `users_export_${exportStartDate}_to_${exportEndDate}.csv`.replace(/:/g, "-");
        downloadCSV(csv, filename);
      } else {
        const exportData = await AdminController.getUsageExport(startDateISO, endDateISO);
        const exportColumns: { key: keyof Dict; label: string }[] = [
          { key: "name", label: "Agency Name" },
          { key: "creationDate", label: "Creation Date" },
          { key: "userCount", label: "User Count" },
          { key: "policiesCount", label: "Policies Count" },
          { key: "comparisonsCount", label: "Comparisons Count" },
          { key: "proposalsCount", label: "Proposals Count" },
          { key: "contractReviewsCount", label: "Contract Reviews Count" },
          { key: "gapAnalysisCount", label: "Coverage Gap Analysis Count" },
          { key: "singlePolicyAnalysisCount", label: "Single Policy Analysis Count" },
          { key: "sovCount", label: "SOV Count" },
          { key: "lossRunsCount", label: "Loss Runs Count" },
          { key: "coiCount", label: "COI Count" },
          { key: "chatsCount", label: "Chats Count" },
        ];
        const csv = arrayToCSV(exportData, exportColumns);
        const filename = `agencies_export_${exportStartDate}_to_${exportEndDate}.csv`.replace(/:/g, "-");
        downloadCSV(csv, filename);
      }
    } catch (e) {
      console.error("Failed to export data with date range", e);
      alert("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Page>
        {/* Header Section */}
        <div className="export-page-header" style={{ 
          width: "100%", 
          maxWidth: "80vw", 
          marginBottom: 24, 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          padding: "0 16px",
          boxSizing: "border-box" as const,
          flexWrap: "wrap" as const,
          gap: 8,
          margin: "0 auto 24px",
        }}>
          <h1 style={{ 
            fontSize: "clamp(32px, 6vw, 49px)", 
            margin: 0,
            flex: "1 1 auto",
            minWidth: 0,
            fontWeight: 600,
            color: "#1a1a1a",
          }}>
            Export Data
          </h1>
          <div style={{ 
            flexShrink: 0, 
            display: "flex", 
            alignItems: "center", 
            gap: 12,
            flexWrap: "wrap" as const,
          }}>
            <button
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                background: "#6b7280",
                color: "#fff",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                transition: "background-color 0.2s ease",
              }}
              onClick={() => navigate("/")}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#4b5563";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#6b7280";
              }}
            >
              Back to Home
            </button>
            <LogoutButton />
          </div>
        </div>
        <div className="export-form-container" style={{ 
          marginBottom: 32, 
          padding: 20, 
          border: "1px solid #e0e0e0", 
          borderRadius: 8,
          backgroundColor: "#f9fafb"
        }}>
          <h2 style={{ fontSize: "clamp(20px, 4vw, 24px)", margin: 0, marginBottom: 16 }}>
            Export {exportMode === "agency" ? "Agency" : "User"} Data (CSV)
          </h2>
          <p style={{ margin: 0, marginBottom: 16, color: "#666", fontSize: "clamp(14px, 2vw, 16px)" }}>
            {exportMode === "agency"
              ? "Export agency data including policies count and workflow counts for a specific date range."
              : "Export per-user data including activity counts and roles for a specific date range."}
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 4, background: "#f0f0f0", borderRadius: 6, padding: 3, marginBottom: 16, width: "fit-content" }}>
            <button
              onClick={() => setExportMode("agency")}
              style={{
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: 500,
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                background: exportMode === "agency" ? "#3949ab" : "transparent",
                color: exportMode === "agency" ? "#fff" : "#555",
                transition: "all 0.15s ease",
              }}
            >
              Agency
            </button>
            <button
              onClick={() => setExportMode("users")}
              style={{
                padding: "6px 14px",
                fontSize: 13,
                fontWeight: 500,
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                background: exportMode === "users" ? "#3949ab" : "transparent",
                color: exportMode === "users" ? "#fff" : "#555",
                transition: "all 0.15s ease",
              }}
            >
              Users
            </button>
          </div>
          <div className="export-form-controls" style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div className="export-form-field" style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 auto", minWidth: 200 }}>
              <label style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
                Start Date
              </label>
              <input
                type="datetime-local"
                value={exportStartDate}
                onChange={(e) => setExportStartDate(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  fontSize: 16,
                  width: "100%",
                  boxSizing: "border-box" as const,
                }}
              />
            </div>
            <div className="export-form-field" style={{ display: "flex", flexDirection: "column", gap: 4, flex: "1 1 auto", minWidth: 200 }}>
              <label style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
                End Date
              </label>
              <input
                type="datetime-local"
                value={exportEndDate}
                onChange={(e) => setExportEndDate(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 4,
                  border: "1px solid #ccc",
                  fontSize: 16,
                  width: "100%",
                  boxSizing: "border-box" as const,
                }}
              />
            </div>
            <button
              className="export-button"
              style={{
                padding: "10px 18px",
                borderRadius: 6,
                border: "none",
                background: isExporting ? "#9ca3af" : "#10b981",
                color: "#fff",
                fontSize: 16,
                cursor: isExporting ? "not-allowed" : "pointer",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                height: "fit-content",
                flexShrink: 0,
              }}
              onClick={handleExportCSVWithDateRange}
              disabled={isExporting}
            >
              {isExporting ? "Exporting..." : "Export CSV"}
            </button>
          </div>
        </div>
      </Page>
    </>
  );
};

export default ExportDataPage;

